import { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchConversationIdsLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import {
  SearchableConversationFilterInput,
  SearchableConversationSortInput
} from 'generated-graphql/types';
import { useDrawer } from 'services/drawer/hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';

type Props = {
  filter: SearchableConversationFilterInput;
  sort: SearchableConversationSortInput;
  conversationIds: string[];
  total: number;
  pageFrom: number;
};

export const useConversationNavigation = ({
  filter,
  sort,
  conversationIds,
  total,
  pageFrom
}: Props) => {
  const navigate = useNavigate();
  const { updateDrawerComponentProps } = useDrawer();

  const [globalIndex, setGlobalIndex] = useState<number>(-1);

  // The drawer keeps the moveToConversation it was opened with, so it must read
  // the current position and page, not the ones captured when it opened.
  const latest = useRef<{
    globalIndex: number;
    indexConversationId?: string;
    filter: SearchableConversationFilterInput;
    sort: SearchableConversationSortInput;
    conversationIds: string[];
    total: number;
    pageFrom: number;
  }>({
    globalIndex,
    filter,
    sort,
    conversationIds,
    total,
    pageFrom
  });
  useLayoutEffect(() => {
    Object.assign(latest.current, {
      filter,
      sort,
      conversationIds,
      total,
      pageFrom
    });
  });

  const setIndex = useCallback((index: number, conversationId?: string) => {
    latest.current.globalIndex = index;
    latest.current.indexConversationId = conversationId;
    setGlobalIndex(index);
  }, []);

  // A position past the loaded page is only known for the conversation the
  // arrows reached. Browser Back can reopen another one.
  const positionOf = useCallback((conversationId: string) => {
    const { conversationIds, pageFrom, globalIndex, indexConversationId } =
      latest.current;
    const localIndex = conversationIds.indexOf(conversationId);

    if (localIndex !== -1) return pageFrom + localIndex;
    return indexConversationId === conversationId ? globalIndex : -1;
  }, []);

  const [fetchConversationIds] = useSearchConversationIdsLazyQuery();

  const updateGlobalIndex = useCallback(
    (conversationId: string) =>
      setIndex(positionOf(conversationId), conversationId),
    [positionOf, setIndex]
  );

  const moveToConversation = useCallback(
    async (direction: 'next' | 'previous') => {
      const { globalIndex, filter, sort, conversationIds, total, pageFrom } =
        latest.current;

      if (globalIndex === -1) return;

      const newGlobalIndex =
        direction === 'next' ? globalIndex + 1 : globalIndex - 1;

      if (newGlobalIndex < 0 || newGlobalIndex >= total) return;

      // Check if the target is within current page
      const localIndex = newGlobalIndex - pageFrom;
      const isWithinCurrentPage =
        localIndex >= 0 && localIndex < conversationIds.length;

      let targetConversationId: string | undefined;

      if (isWithinCurrentPage) {
        targetConversationId = conversationIds[localIndex];
      } else {
        // Fetch the target conversation from the appropriate position
        const result = await fetchConversationIds({
          variables: {
            filter,
            sort,
            from: newGlobalIndex,
            limit: 1
          }
        });

        const items = extractQueryListItems(result.data);
        targetConversationId = items[0]?.id;
      }

      if (targetConversationId) {
        setIndex(newGlobalIndex, targetConversationId);
        navigate(`./?conversationId=${targetConversationId}`);
        updateDrawerComponentProps({
          conversationId: targetConversationId,
          conversationIndex: newGlobalIndex
        });
      }
    },
    [fetchConversationIds, navigate, updateDrawerComponentProps, setIndex]
  );

  return {
    total,
    globalIndex,
    positionOf,
    moveToConversation,
    updateGlobalIndex
  };
};
